import { use } from "react"
import { UNSAFE_DataRouterContext, useLocation } from "react-router"

import { buildUrlSearchParamsString } from "./url-utils"

/**
 * Search-param writes that share a location before that location commits.
 * MSW 3 resolves mocked loaders asynchronously, so a second `setSearchParams`
 * in the same turn would otherwise replace the URL from the still-committed
 * query string and cancel the first navigation.
 *
 * Pending state is stored per data router (or, outside a data router, on the
 * location object). It is dropped when the location key changes or when the
 * committed query string moves off the string the write was staged from.
 */
type SetSearchParams = (
  nextInit: string,
  navigateOptions?: {
    replace?: boolean
  }
) => void

interface PendingSearch {
  baseSearch: string
  locationKey: string
  search: string
}

interface StageSearchParamUpdatesOptions {
  committed: URLSearchParams
  locationKey: string
  replace: boolean
  scope: object
  setSearchParams: SetSearchParams
  updates: Record<string, unknown>
}

const pendingByScope = new WeakMap<object, PendingSearch>()

const isClearedSearchValue = (value: unknown): boolean =>
  value == null || value === ""

const committedSearch = (committed: URLSearchParams): string =>
  buildUrlSearchParamsString({}, committed)

const activePending = (
  scope: object,
  locationKey: string,
  committed: string
): PendingSearch | null => {
  const pending = pendingByScope.get(scope)
  if (pending?.locationKey !== locationKey) {
    pendingByScope.delete(scope)
    return null
  }

  if (committed !== pending.baseSearch) {
    pendingByScope.delete(scope)
    return null
  }

  return pending
}

export const useSearchParamStagingScope = (): object => {
  // React Router 8 only exposes this context under the UNSAFE_ name.
  const routerContext = use(UNSAFE_DataRouterContext)
  const location = useLocation()

  return routerContext?.router ?? location
}

export const stageSearchParamUpdates = ({
  committed,
  locationKey,
  replace,
  scope,
  setSearchParams,
  updates,
}: StageSearchParamUpdatesOptions): void => {
  const baseSearch = committedSearch(committed)
  const pending = activePending(scope, locationKey, baseSearch)
  const base =
    pending !== null ? new URLSearchParams(pending.search) : committed
  const nextParams = Object.fromEntries(
    Object.entries({
      ...Object.fromEntries(base.entries()),
      ...updates,
    }).filter(([, value]) => !isClearedSearchValue(value))
  )

  const next = buildUrlSearchParamsString(nextParams)
  const current = pending?.search ?? baseSearch

  if (next === current) {
    return
  }

  pendingByScope.set(scope, {
    baseSearch: pending?.baseSearch ?? baseSearch,
    locationKey,
    search: next,
  })
  setSearchParams(next, { replace })
}
